import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../context/auth/auth-context'
import axios from 'axios'
import { type AlertColor, Tooltip, Button } from '@mui/material'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { useHeartbeats } from '../../../hooks/data/use-heartbeats'
import { formAlertErrorMessage, handleRaceSettleAllResponse, isEmptyString } from '../../../utils/string-utils'
import ConfirmationModal from '../../../components/modal/confirmation-modal'
import { isUsingProdDataRoutes } from '../../../utils/api/servers'
import { ExtendedPromise } from '../../../utils/promises-utils'

export interface PauseTradingButtonProps {
  strategyId: string
  strategyName: string
  isApiConnected: boolean
  controlExecuteCommandUrl: string | null
  displayToastWithMessage: (message: string, severity: AlertColor) => void
}

const PauseTradingButton: React.FC<PauseTradingButtonProps> = (props) => {
  const { strategyId, strategyName, isApiConnected, controlExecuteCommandUrl, displayToastWithMessage } = props
  const useProdDataRoutes = isUsingProdDataRoutes()
  const isDisabled = !isApiConnected || isEmptyString(controlExecuteCommandUrl)

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const openModal = useCallback((): void => {
    setIsModalOpen(true)
  }, [])
  const closeModal = useCallback((): void => {
    setIsModalOpen(false)
  }, [])

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const [isHeartbeatFound, setIsHeartbeatFound] = useState<boolean>(false)
  const heartbeats = useHeartbeats(isAuthorized, accessToken)
  useEffect(() => {
    const matchingHeartbeat = heartbeats.find(heartbeat => heartbeat.strategyId === strategyId)
    setIsHeartbeatFound(matchingHeartbeat != null)
  }, [heartbeats])

  const toggleTradingState = useCallback(() => {
    if (controlExecuteCommandUrl == null || controlExecuteCommandUrl.length === 0) {
      console.debug('[Strategy] Failed to toggle trading status (reason: invalid url)')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    const missingStrategyId = useProdDataRoutes && isEmptyString(strategyId)
    if (missingStrategyId) {
      console.debug('[Strategy] No strategy data found - skipping action')
      displayToastWithMessage('No strategy data found - skipping action', 'warning')
      return
    }

    const missingHeartbeatProd = useProdDataRoutes && !isHeartbeatFound
    if (missingHeartbeatProd) {
      console.debug('[Strategy] No strategy heartbeat found - skipping action')
      displayToastWithMessage('No strategy heartbeat found - skipping action', 'error')
      return
    }

    const message = {
      timestamp: new Date().toISOString(),
      strategyId: useProdDataRoutes ? strategyId : strategyName,
      command: 'pauseTrading',
    }

    const promise = axios
      .post(controlExecuteCommandUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })

    ExtendedPromise
      .raceSettleAll([promise], 5000, new Error('timeout'))
      .then(response => {
        console.debug(response)
        handleRaceSettleAllResponse(response, false, displayToastWithMessage)
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
  }, [strategyId, strategyName, accessToken, isHeartbeatFound])

  return (
    <>
      <ConfirmationModal
        open={isModalOpen}
        onClose={closeModal}
        onHandle={toggleTradingState}
        title={`Pause "${strategyName}" strategy?`}
        description='All live orders will be cancelled. Pause the strategy now?'
        handleButtonLabel='Pause strategy'
        handleButtonColour='error'
      />

      <Tooltip title={(useProdDataRoutes && !isHeartbeatFound) ? 'No heartbeat found' : 'Pause trading'}>
        <span>
          <Button
            startIcon={<PauseCircleOutlineIcon/>}
            variant='outlined'
            size='small'
            color={(useProdDataRoutes && !isHeartbeatFound) || isDisabled ? 'inherit' : 'error'}
            disabled={(useProdDataRoutes && !isHeartbeatFound) || isDisabled}
            onClick={openModal}
          >
            Pause
          </Button>
        </span>
      </Tooltip>
    </>
  )
}

export default React.memo(PauseTradingButton)
